import React from "react"
import {
	Flex,
	Text,
	Button,
	FilterCheckbox,
	Accordion,
	INNER_MAX_WIDTH,
} from "@capioweb/ui-components"
import { LocalesContext } from "../../../context"
import AnimateHeight from "react-animate-height"
import { NewsArchiveFilterContext } from "../context/NewsArchiveContext"
import { NewsProps } from ".."
import { Category } from "../../../@types/CapioWeb/core"
import { sendOpenFilterDataLayerPush } from "../../../utils"

interface NewsArchiveSectionProps {
	filterType: "organisation" | "subject"
	heading?: string
	filterValues: Category[]
	lastChild?: boolean
}

const FilterSection: React.FC<NewsArchiveSectionProps> = ({
	filterType,
	heading,
	filterValues,
	lastChild,
}) => {
	const {
		activeOrganisationIds,
		activeNewsSubjectIds,
		updateActiveOrganisationIds,
		updateActiveNewsSubjectIds,
	} = React.useContext(NewsArchiveFilterContext)

	return (
		<Flex
			w={1}
			flexDirection="column"
			pb={1.5}
			mb={{ _: 0, l: lastChild ? 2 : 3 }}
			borderBottom={lastChild ? false : { _: false, l: true }}
		>
			{heading && (
				<Text
					fontSize={1.75}
					lineHeight={2.5}
					fontWeight="bold"
					letterSpacing="1px"
					textTransform="uppercase"
					mb={1.5}
				>
					{heading}
				</Text>
			)}
			{filterValues?.length > 0 && (
				<Flex w={1} flexWrap="wrap">
					{filterValues.map((filter, i) => {
						return (
							<Flex pb={1.5} pr={1.5} key={filter.id}>
								<FilterCheckbox
									checked={
										activeOrganisationIds.includes(Number(filter.id)) ||
										activeNewsSubjectIds.includes(Number(filter.id))
									}
									label={filter.name}
									value={filter.id.toString()}
									onChange={() =>
										filterType === "organisation"
											? updateActiveOrganisationIds(Number(filter.id))
											: updateActiveNewsSubjectIds(Number(filter.id))
									}
									withoutBorder={true}
								/>
							</Flex>
						)
					})}
				</Flex>
			)}
		</Flex>
	)
}

const NewsArchiveFilter: React.FC<NewsProps> = ({
	organisationsFilterValues,
	subjectsFilterValues,
}) => {
	const { translate, translationKeys } = React.useContext(LocalesContext)
	// Toggle for filter
	const [filterOpen, setFilterOpen] = React.useState(false)

	return (
		<>
			<Flex w={1} py={3} justifyContent="center" backgroundColor="blue11">
				<Button
					variant="ghost-navy"
					icon="filter"
					textColor="navy"
					onClick={() => {
						setFilterOpen(!filterOpen)
						sendOpenFilterDataLayerPush(filterOpen ? "close" : "open")
					}}
				>
					{`${
						filterOpen
							? translate(translationKeys.close)
							: translate(translationKeys.open)
					} ${translate(translationKeys.filter)}`}
				</Button>
			</Flex>
			<AnimateHeight height={filterOpen ? "auto" : 0}>
				<Flex w={1} px={3} backgroundColor="blue11">
					<Flex w={1} flexDirection="column" maxw={INNER_MAX_WIDTH} m="0 auto">
						{/* Filter layout for screens >= l */}
						<Flex w={1} display={{ _: "none", l: "block" }}>
							{/* Organisation filters */}
							{organisationsFilterValues.length > 0 && (
								<FilterSection
									filterType="organisation"
									heading={translate(translationKeys.organisation)}
									filterValues={organisationsFilterValues}
									lastChild={false}
								/>
							)}

							{/* News subject filters */}
							{subjectsFilterValues?.length > 0 && (
								<FilterSection
									filterType="subject"
									heading={translate(translationKeys.subjects)}
									filterValues={subjectsFilterValues}
									lastChild={true}
								/>
							)}
						</Flex>

						{/* Filter layout for screens < l */}
						<Flex
							w={1}
							display={{ _: "flex", l: "none" }}
							flexDirection="column"
							pb={4.5}
						>
							<Accordion>
								{/* Organisation filters */}
								<Accordion.Header id={translate(translationKeys.organisation)}>
									<Text fontSize={2.5} lineHeight={3} fontWeight="bold" mb={0}>
										{translate(translationKeys.organisation)}
									</Text>
								</Accordion.Header>
								<Accordion.Panel id={translate(translationKeys.organisation)}>
									<FilterSection
										filterType="organisation"
										filterValues={organisationsFilterValues}
									/>
								</Accordion.Panel>

								{/* News subject filters */}
								{subjectsFilterValues.length > 0 && (
									<>
										<Accordion.Header id={translate(translationKeys.subjects)}>
											<Text
												fontSize={2.5}
												lineHeight={3}
												fontWeight="bold"
												mb={0}
											>
												{translate(translationKeys.subjects)}
											</Text>
										</Accordion.Header>
										<Accordion.Panel id={translate(translationKeys.subjects)}>
											<Flex flexDirection="column" px={2} pt={1}>
												<FilterSection
													filterType="subject"
													filterValues={subjectsFilterValues}
													lastChild
												/>
											</Flex>
										</Accordion.Panel>
									</>
								)}
							</Accordion>
						</Flex>
					</Flex>
				</Flex>
			</AnimateHeight>
		</>
	)
}

export { NewsArchiveFilter }
